import React from "react";
import { useState, useEffect } from "react";
import { Form, Button } from "react-bootstrap";
import _ from "lodash";
import { Spinner } from "react-bootstrap";

export default function ImportLdnsForm(props) {
  const [networkOptions, setNetworkOptions] = useState([]);
  const [manufacturer, setManufacturer] = useState(null);
  const [network, setNetwork] = useState("");
  const [inputFile, setInputFile] = useState(null);

  const [fileValidated, setFileValidated] = useState(false);
  const [errorMessages, setErrorMessages] = useState([]);
  const [errorCount, setErrorCount] = useState(0);
  const [fileResults, setFileResults] = useState({});
  const [isBusy, setIsBusy] = useState(false);
  const [busyAction, setBusyAction] = useState(null);

  function fileIsXLSX(selectedFile) {
    if (selectedFile) {
      const extName = selectedFile.files[0].name.split(".").pop();
      if (extName != "xlsx") {
        alert("File must have extension .xlsx");
        selectedFile.value = null;
      }
      setInputFile(selectedFile.files[0]);
      setFileValidated(false);
    }
  }

  function validateDisabled() {
    return !(manufacturer && inputFile && !isBusy && network);
  }

  function submitDisabled() {
    return !(fileValidated && network && manufacturer && !isBusy);
  }

  // Only show first three error messages
  const renderErrors = () =>
    _.map(errorMessages.slice(0, 3), (errorMessage, index) => (
      <li key={`error${index}`}>
        <i className="fa fa-circle-xmark" />
        <span className="p-3">{errorMessage}</span>
      </li>
    ));

  const renderStatusMessage = () => {
    return fileResults ? (
      <>
        <span>
          File contained {fileResults.row_count} rows; database contained{" "}
          {fileResults.existing_ldns_count} rows.
        </span>
        <br />
        <span>{fileResults.found_ldns_count} LDN(s) matched, </span>
        <span className="text-success">
          {fileResults.created_ldns_count} LDN(s) would be created,{" "}
        </span>
        <span className="text-danger">
          {fileResults.deleted_ldns_count} LDN(s) would be deleted
        </span>
      </>
    ) : null;
  };

  async function handleVerify(event) {
    event.preventDefault();

    if (!inputFile || isBusy) return;

    setIsBusy(true);
    setBusyAction("verify");
    const url = props.validate_url;
    const formData = new FormData();
    formData.append("file", inputFile);
    formData.append("manufacturer", manufacturer);
    formData.append("network", network);
    try {
      const response = await fetch(url, {
        method: "POST",
        headers: {
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]').getAttribute("content"),
        },
        body: formData,
      });
      const data = await response.json();
      if (!response.ok) {
        setErrorCount(data.error_count);
        setErrorMessages(data.message);
        setFileValidated(false);
        setFileResults({});
      } else {
        setErrorCount(0);
        setErrorMessages([]);
        setFileValidated(true);
        setFileResults(data);
      }
      setBusyAction(null);
      setIsBusy(false);
    } catch (error) {
      setErrorCount(1);
      setErrorMessages(["Unknown error encountered"]);
      setFileValidated(false);
      setFileResults({});

      setBusyAction(null);
      setIsBusy(false);
    }
  }

  function handleSubmit(event) {
    event.preventDefault();

    if (isBusy || !inputFile) return;

    setIsBusy(true);
    setBusyAction("submit");

    const url = props.upload_url;
    const formData = new FormData();
    formData.append("file", inputFile);
    formData.append("manufacturer", event.target.manufacturer.value);
    formData.append("network", event.target.network.value);

    fetch(url, {
      method: "POST",
      headers: {
        "X-CSRF-Token":
          document.querySelector('meta[name="csrf-token"]')?.getAttribute("content") ?? "",
      },
      body: formData,
    })
      .then((res) => {
        if (res.status >= 200 && res.status <= 299) {
          location.reload();
          return;
        }
        setBusyAction(null);
        setIsBusy(false);
      })
      .catch((_err) => {
        setBusyAction(null);
        setIsBusy(false);
      });
  }

  return (
    <React.Fragment>
      <Form onSubmit={handleSubmit} id="pidUploadForm">
        <Form.Group className="mb-3" controlId="manufacturer">
          <Form.Label>
            Manufacturer <small className="text-danger">*</small>
          </Form.Label>
          <Form.Select
            required
            name="manufacturer"
            onChange={(e) => {
              setManufacturer(e.target.value);
              const mfrNetwork = props.manufacturer_networks.find(
                (mn) => mn.manufacturer === e.target.value
              );
              setNetworkOptions(mfrNetwork?.networks || []);
              setNetwork("");
            }}
          >
            <option value="">{"< Make a Selection >"}</option>
            {props.manufacturers.map((manufacturer) => (
              <option key={manufacturer.key} value={manufacturer.key}>
                {manufacturer.name}
              </option>
            ))}
          </Form.Select>
        </Form.Group>
        <Form.Group className="mb-3" controlId="network">
          <Form.Label>
            Network <small className="text-danger">*</small>
          </Form.Label>
          <Form.Select
            required
            name="network"
            value={network}
            onChange={(e) => setNetwork(e.target.value)}
          >
            <option value="">{"< Make a Selection >"}</option>
            {manufacturer == "novo_nordisk" || manufacturer == "boehringer_ingelheim" ? (
              <option key="ldn" value="ldn">
                LDN
              </option>
            ) : (
              <>
                {networkOptions.map((network) => (
                  <option key={network} value={network}>
                    {network}
                  </option>
                ))}
              </>
            )}
          </Form.Select>
        </Form.Group>
        <Form.Group
          controlId="headers"
          className="mb-3 p-3 bg-body-tertiary rounded border shadow-sm"
        >
          <Form.Label className="text-muted smaller">Required LDN File Headers:</Form.Label>
          <br />
          <b className="text-primary">MANUFACTURER, NETWORK, PID</b>
          <br />
          <Form.Label className="text-muted smaller">Required LDN File Type:</Form.Label>
          <br />
          <b className="text-primary">.xlsx (Excel)</b>
          <br />
          <Form.Label className="text-muted smaller">File To Upload:</Form.Label>
          <p className="smaller">
            <span>
              Warning: This file is meant to be a complete list of LDNs for this manufacturer and
              network.{" "}
            </span>
            <span className="text-danger">
              Any LDN not in the file for this manufacturer and network will be <b>removed</b>.
            </span>
          </p>
          <Form.Control required name="file" type="file" onChange={(e) => fileIsXLSX(e.target)} />
        </Form.Group>

        {errorMessages.length > 0 ? (
          <>
            <p className="text-danger">
              {errorCount} row(s) had errors (first 3 are shown):
              <br />
            </p>
            <ul className="text-danger" role="list">
              {renderErrors()}
            </ul>
          </>
        ) : null}
        {fileValidated ? <div className="mb-3">File validated: {renderStatusMessage()}</div> : null}
        <Button onClick={handleVerify} disabled={validateDisabled()}>
          {busyAction === "verify" ? (
            <>
              <Spinner
                animation="border"
                variant="light"
                className="text-muted"
                as="span"
                size="sm"
              />
              Checking...
            </>
          ) : (
            "Check LDN File"
          )}
        </Button>
        <Button
          type="submit"
          variant="success"
          style={{ marginLeft: "8px" }}
          disabled={submitDisabled()}
        >
          {busyAction === "submit" ? (
            <>
              <Spinner
                animation="border"
                variant="light"
                className="text-muted"
                as="span"
                size="sm"
              />
              Submitting...
            </>
          ) : (
            "Submit LDN File"
          )}
        </Button>
      </Form>
    </React.Fragment>
  );
}
